"use client";

import {useTranslation} from "react-i18next";
import type {MutationProposal} from "@/services/wingman/mutation-proposal";
import Icon from "@/components/library/Icon";
import ScriptApprovalHeader from "./ScriptApprovalHeader";
import {changedFields, ScriptSavedContent} from "./ScriptSnapshotDiff";
import type {ReactNode} from "react";

export function OpenScriptButton({onOpen}: {onOpen?: () => Promise<void>}) {
   const {t} = useTranslation("ask");
   return onOpen ? <button type="button" onClick={() => void onOpen()} className="approval-open inline-flex items-center gap-1 rounded text-approval-body font-medium">
      {t("toolApproval.scriptReview.openScript")}<Icon icon="arrow-up-right-from-square" className="h-3 w-3 shrink-0" />
   </button> : null;
}

/** Only verified execution renders the saved receipt. Approval acknowledgement is still saving. */
export default function ScriptApprovalReceipt({proposal, status, saved, onOpen, notices}: {
   proposal: MutationProposal;
   status: "saving" | "completed" | "denied";
   saved: Record<string, unknown> | null;
   onOpen?: () => Promise<void>;
   notices?: ReactNode;
}) {
   const {t} = useTranslation("ask");
   const fields = changedFields(proposal);
   const greeting = fields.length === 1 && fields[0] === "greeting";
   const label = t("toolApproval.title");
   if (status === "saving") return <section aria-label={label} className="approval-card my-3 rounded-xl border px-4 py-3.5">
      <output className="flex items-center gap-2.5 text-sm font-medium">
         <span aria-hidden className="approval-spinner h-3.5 w-3.5 shrink-0 rounded-full border-2 motion-safe:animate-spin" />
         {t(greeting ? "toolApproval.scriptReview.savingGreeting" : "toolApproval.scriptReview.saving")}
      </output>
   </section>;
   if (status === "denied") return <section aria-label={label} className="approval-card approval-rejected my-3 rounded-xl border">
      <div className="flex items-start gap-2.5 px-3.5 py-3">
         <span className="approval-rejected-icon mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"><Icon icon="xmark" className="h-3 w-3" /></span>
         <div className="min-w-0">
            <output className="text-sm font-medium">{t("toolApproval.scriptReview.rejected")}</output>
            <p className="text-xs leading-approval-meta text-800">{t(greeting ? "toolApproval.scriptReview.greetingUnchanged" : "toolApproval.scriptReview.scriptUnchanged")}</p>
         </div>
      </div>
      {notices}
   </section>;
   return <section aria-label={label} className="approval-card my-3 overflow-hidden rounded-xl border">
      <ScriptApprovalHeader proposal={proposal} status="completed" statusKey="toolApproval.completed" queued={null} />
      <ScriptSavedContent proposal={proposal} saved={saved} />
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5">
         <span className="text-xs text-600">{t("toolApproval.scriptReview.recorded")}</span>
         <OpenScriptButton onOpen={onOpen} />
      </footer>
   </section>;
}
